import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { formatApiError } from "@/lib/api";
import { whatsappLink } from "@/lib/contact";
import { toast } from "sonner";

export default function CartSheet() {
  const { items, removeItem, updateQty, clear, total, open, setOpen } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", note: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Моля, попълнете име и телефон");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          size_label: i.size_label,
          price: i.price,
          qty: i.qty,
        })),
      });
      clear();
      setDone(true);
      setCheckout(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setCheckout(false);
      setDone(false);
    }, 300);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetContent className="bg-cream border-line w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 py-5 border-b border-line">
          <SheetTitle className="font-serif text-2xl text-ink text-left">
            {done ? "Благодарим!" : checkout ? "Данни за поръчка" : "Вашата количка"}
          </SheetTitle>
        </SheetHeader>

        {done ? (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-5">
            <div className="w-16 h-16 rounded-full bg-clay/15 flex items-center justify-center text-clay font-serif text-3xl">
              ✦
            </div>
            <p className="text-stone font-light leading-relaxed" data-testid="order-success">
              Вашата поръчка е получена. Айлян ще се свърже с вас скоро за потвърждение и доставка.
            </p>
            <button
              onClick={close}
              className="bg-ink text-cream px-8 py-3 rounded-full text-sm hover:bg-clay transition-colors duration-300"
            >
              Затвори
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <p className="text-stone font-light">Количката ви е празна.</p>
            <button
              onClick={close}
              className="text-clay text-sm underline underline-offset-4"
            >
              Разгледай колекцията
            </button>
          </div>
        ) : checkout ? (
          <form onSubmit={submit} className="flex-1 flex flex-col overflow-y-auto">
            <div className="px-6 py-6 space-y-4 flex-1">
              {[
                { k: "name", label: "Име и фамилия *", type: "text" },
                { k: "phone", label: "Телефон *", type: "tel" },
                { k: "email", label: "Имейл", type: "email" },
                { k: "address", label: "Адрес за доставка", type: "text" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs uppercase tracking-[0.15em] text-stone">{f.label}</label>
                  <input
                    type={f.type}
                    value={form[f.k]}
                    onChange={set(f.k)}
                    className="mt-2 w-full bg-sand border border-line rounded-lg px-4 py-3 text-ink outline-none focus:border-clay transition-colors"
                    data-testid={`checkout-${f.k}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-stone">Бележка</label>
                <textarea
                  value={form.note}
                  onChange={set("note")}
                  rows={3}
                  className="mt-2 w-full bg-sand border border-line rounded-lg px-4 py-3 text-ink outline-none focus:border-clay transition-colors resize-none"
                  data-testid="checkout-note"
                />
              </div>
            </div>
            <div className="border-t border-line px-6 py-5">
              <div className="flex justify-between mb-4">
                <span className="text-stone">Общо</span>
                <span className="font-serif text-2xl text-ink">{total} лв</span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-clay text-cream py-4 rounded-full text-sm font-medium hover:bg-claydark transition-colors duration-300 disabled:opacity-60"
                data-testid="place-order-button"
              >
                {submitting ? "Изпращане…" : "Изпрати поръчката"}
              </button>
              <button
                type="button"
                onClick={() => setCheckout(false)}
                className="w-full text-stone text-sm mt-3"
              >
                ← Назад към количката
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-line">
              {items.map((i) => (
                <div key={i.key} className="py-4 flex gap-4" data-testid={`cart-item-${i.key}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-lg bg-sand" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-serif text-lg text-ink leading-tight">{i.name}</h4>
                        <p className="text-xs text-stone mt-0.5">Размер: {i.size_label}</p>
                      </div>
                      <button onClick={() => removeItem(i.key)} aria-label="Премахни" data-testid={`remove-${i.key}`}>
                        <Trash2 className="w-4 h-4 text-stone hover:text-destructive transition-colors" />
                      </button>
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex items-center border border-line rounded-full">
                        <button onClick={() => updateQty(i.key, i.qty - 1)} className="px-2.5 py-1.5" aria-label="Намали">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm w-6 text-center">{i.qty}</span>
                        <button onClick={() => updateQty(i.key, i.qty + 1)} className="px-2.5 py-1.5" aria-label="Увеличи">
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="font-medium text-ink">{i.price * i.qty} лв</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-line px-6 py-5">
              <div className="flex justify-between mb-4">
                <span className="text-stone">Общо</span>
                <span className="font-serif text-2xl text-ink" data-testid="cart-total">{total} лв</span>
              </div>
              <button
                onClick={() => setCheckout(true)}
                className="w-full bg-ink text-cream py-4 rounded-full text-sm font-medium hover:bg-clay transition-colors duration-300"
                data-testid="checkout-button"
              >
                Към поръчка
              </button>
              <a
                href={whatsappLink(
                  "Здравейте, Айлян! Бих искал(а) да поръчам:\n" +
                    items.map((i) => `• ${i.name} (${i.size_label}) × ${i.qty} — ${i.price * i.qty} лв`).join("\n") +
                    `\nОбщо: ${total} лв`
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 rounded-full text-sm font-medium hover:brightness-95 transition-all"
                data-testid="whatsapp-order-button"
              >
                Поръчай директно през WhatsApp
              </a>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
